import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import debounce from 'lodash/debounce';

import { ARROW_DOWN, ARROW_UP } from 'approot/shared/ally/keyboard.hooks';
import { usePrevious } from 'approot/shared/hooks/use-previous.hooks';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import type { ComponentDimensionInput } from '../../../../../gql/schema-types';
import { computePaddingTop } from './component-dimension.utils';
import * as Styled from './component-dimensions.style';
import {
  COMPONENT_DIMENSION_ID_PREFIX,
  Dimensions,
} from './component-dimensions.constants';
import IconMinusInCircle from 'approot/shared/icons/icon-minus-in-circle';
import IconPlusInCircle from 'approot/shared/icons/icon-plus-in-circle';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import { ACTIVITY_BUILDER_COMPONENT_MENU_EXTRA } from 'approot/activity-builder/activity-builder.constants';

export const MIN_HEIGHT = 5;

export const ComponentDimensions = ({
  id,
  children,
  defaultValue,
  showHandle,
  isLoading,
  extraHeight,
  onResize,
  onResizeStart,
  onResizeEnd,
}: {
  id: string;
  children: ReactNode;
  defaultValue: ComponentDimensionInput;
  showHandle?: boolean;
  isLoading?: boolean;
  extraHeight?: number;
  onResizeStart?: () => void;
  onResize?: (width: number, height: number) => void;
  onResizeEnd?: (width: number, height: number) => void;
}) => {
  const [height, setHeight] = useState<number>(0);
  const [canShowDimensions, setCanShowDimenstions] = useState<boolean>(false);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const { slideScaleRatio } = useActivityBuilderContext();

  const debounceKeyboardDown = useMemo(
    () =>
      debounce((width: number, height: number) => {
        onResizeEnd?.(width, height);
      }, 300),
    [onResizeEnd]
  );

  const handleKeyboardKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const key = e.key;
      if (key === ARROW_UP || key === ARROW_DOWN) {
        e.preventDefault();
        setIsDragging(true);
        onResizeStart?.();

        const isUp = key === ARROW_UP;
        const initialWidth = containerRef?.offsetWidth || 0 / slideScaleRatio;
        const initialHeight = containerRef?.offsetHeight || 0 / slideScaleRatio;
        const newHeight = Math.floor(
          Math.max(isUp ? initialHeight - 20 : initialHeight + 20, MIN_HEIGHT)
        );
        setHeight(newHeight);
        onResize?.(initialWidth, newHeight);
        debounceKeyboardDown(initialWidth, newHeight);
      }
    },
    [
      containerRef,
      slideScaleRatio,
      debounceKeyboardDown,
      setHeight,
      onResize,
      onResizeStart,
    ]
  );

  const handleOnKeyDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      onResizeStart?.();

      const startY = e.clientY;
      const initialWidth = containerRef?.offsetWidth || 0 / slideScaleRatio;
      const initialHeight = containerRef?.offsetHeight || 0 / slideScaleRatio;
      setHeight(initialHeight);

      const onMouseMove = (moveEvent: PointerEvent) => {
        const deltaY = (moveEvent.clientY - startY) / slideScaleRatio;
        const newHeight = Math.floor(
          Math.max(initialHeight + deltaY, MIN_HEIGHT)
        );
        setHeight(newHeight);
        onResize?.(initialWidth, newHeight);
      };

      const onMouseUp = () => {
        if (containerRef) {
          onResizeEnd?.(initialWidth, containerRef.offsetHeight);
        }

        document.removeEventListener('pointermove', onMouseMove, false);
        document.removeEventListener('pointerup', onMouseUp, false);
      };

      document.addEventListener('pointermove', onMouseMove, false);
      document.addEventListener('pointerup', onMouseUp, false);
    },
    [
      containerRef,
      slideScaleRatio,
      setHeight,
      setIsDragging,
      onResizeStart,
      onResizeEnd,
      onResize,
    ]
  );

  const handleIncrementHeight = useCallback(
    (newHeight: number) => {
      if (containerRef) {
        const initialWidth = containerRef.offsetWidth || 0 / slideScaleRatio;

        setIsDragging(true);
        setHeight(newHeight);
        onResize?.(initialWidth, newHeight);
        debounceKeyboardDown(initialWidth, newHeight);
      }
    },
    [debounceKeyboardDown, containerRef, onResize, slideScaleRatio]
  );

  const previousAspect = usePrevious(defaultValue.height);
  useEffect(() => {
    if (previousAspect !== defaultValue.height) {
      setIsDragging(false);
      setHeight(0);
    }
    //eslint-disable-next-line
  }, [defaultValue.height]);

  const componentExtraMenuEl = document.getElementById(
    ACTIVITY_BUILDER_COMPONENT_MENU_EXTRA
  );

  useEffect(() => {
    if (showHandle) {
      setCanShowDimenstions(true);
    } else {
      setCanShowDimenstions(false);
    }
  }, [showHandle]);

  return (
    <Styled.ComponentHeightWrapper
      id={`${COMPONENT_DIMENSION_ID_PREFIX}${id}`}
      style={{
        height: isDragging ? `${height}px` : 'auto',
        paddingTop: !isDragging ? `${computePaddingTop(defaultValue)}%` : 0,
      }}
      ref={el => {
        if (el && height === 0 && el.offsetHeight > 0) {
          setHeight(el.offsetHeight);
          setContainerRef(el);
        }
      }}
    >
      <Styled.ComponentInnerWrapper $isLoading={isLoading}>
        {children}
      </Styled.ComponentInnerWrapper>
      {showHandle && (
        <>
          <Styled.ComponentHeightBottomHandle
            data-selector="container-height"
            aria-label="Adjust height of container"
            onKeyDown={e => handleKeyboardKeyDown(e)}
            onPointerDown={e => handleOnKeyDown(e)}
          />
          {canShowDimensions &&
            componentExtraMenuEl &&
            createPortal(
              <Styled.ComponentHeightStepWrapper>
                <SecondaryButtonIcon
                  type="button"
                  $isSmall
                  $noBorder
                  disabled={isLoading}
                  onClick={() => {
                    if (containerRef) {
                      const newHeight = containerRef.offsetHeight - 1;
                      if (newHeight > MIN_HEIGHT) {
                        handleIncrementHeight(newHeight);
                      }
                    }
                  }}
                  data-selector="settings-menu-decrement"
                >
                  <IconMinusInCircle width={20} height={20} />
                </SecondaryButtonIcon>
                <Styled.ComponentHeightValue
                  aria-label={`Height: ${height}px`}
                  data-selector="current-height"
                >
                  Height:{' '}
                  {isDragging
                    ? Math.floor(height + (extraHeight || 0)) * 2
                    : ((containerRef?.offsetHeight || 0) + (extraHeight || 0)) *
                      2}
                </Styled.ComponentHeightValue>
                <SecondaryButtonIcon
                  type="button"
                  $isSmall
                  $noBorder
                  disabled={isLoading}
                  onClick={() => {
                    if (containerRef) {
                      handleIncrementHeight(containerRef.offsetHeight + 1);
                    }
                  }}
                  data-selector="settings-menu-increment"
                >
                  <IconPlusInCircle width={20} height={20} />
                </SecondaryButtonIcon>
              </Styled.ComponentHeightStepWrapper>,
              componentExtraMenuEl
            )}
        </>
      )}
    </Styled.ComponentHeightWrapper>
  );
};

export const useComponentDimensions = ({
  defaultValue,
}: {
  defaultValue: ComponentDimensionInput;
}) => {
  const [dimensions, setDimensions] =
    useState<ComponentDimensionInput>(defaultValue);

  return {
    dimensions,
    setDimensions,
  };
};

export const ComponentAspectRatio = ({
  dimensions,
  children,
}: {
  dimensions: Dimensions;
  children: ReactNode;
}) => {
  return (
    <Styled.ComponentAspectRatio
      style={{
        paddingTop: `${computePaddingTop(dimensions)}%`,
      }}
    >
      {children}
    </Styled.ComponentAspectRatio>
  );
};
